import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Globe, Clock, Check, MapPin } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;

const REQUEST_TYPES = [
  "International Sourcing",
  "Consulting",
  "Logistics Coordination",
  "Purchasing & Shipping",
  "General Inquiry",
];

const fieldClass =
  "w-full rounded-none border-0 border-b-2 border-white/20 bg-transparent px-0 py-4 font-mono text-base text-white placeholder:text-white/30 transition-colors duration-300 focus:border-primary focus:outline-none focus:ring-0";
const labelClass = "font-mono text-xs uppercase tracking-[0.2em] text-white/50";

const empty = { name: "", company: "", email: "", phone: "", request_type: "", region: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!form.company.trim()) return "Please enter your company.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.request_type) return "Please select a type of request.";
    if (!form.message.trim()) return "Please add a short message.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      // Static site: submit directly to Web3Forms (client-side FormData avoids CORS preflight).
      const fd = new FormData();
      fd.append("access_key", WEB3FORMS_KEY);
      fd.append("subject", `New ${form.request_type} inquiry from ${form.name.trim()}`);
      fd.append("from_name", "Prescience Service Website");
      fd.append("replyto", form.email.trim());
      fd.append("name", form.name.trim());
      fd.append("company", form.company.trim());
      fd.append("email", form.email.trim());
      fd.append("phone", form.phone.trim() || "—");
      fd.append("request_type", form.request_type);
      fd.append("sourcing_region", form.region.trim() || "—");
      fd.append("message", form.message.trim());

      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      const result = await res.json().catch(() => ({}));

      if (res.ok && result.success === true) {
        setDone(true);
        setForm(empty);
        toast.success("Inquiry received. We'll be in touch shortly.");
      } else {
        console.error("Web3Forms did not accept the submission:", res.status, result);
        toast.error(
          `Your message could not be sent${result.message ? `: ${result.message}` : ""}. Please email us directly at contact@prescience-svcs.com.`
        );
      }
    } catch (err) {
      console.error("Contact submission failed:", err);
      toast.error("Your message could not be sent. Please email us directly at contact@prescience-svcs.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="border-b border-white/10 pt-40 pb-16 md:pt-52 md:pb-20">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-primary">[ Contact ]</p>
          <h1 className="font-heading text-[12vw] font-black uppercase leading-[0.85] tracking-tighter md:text-[8vw]">
            <MaskedLines lines={["Let's source", "your parts"]} />
          </h1>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
          {/* FORM */}
          <div className="md:col-span-7">
            {done ? (
              <motion.div
                data-testid="contact-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-primary bg-primary/10 p-10"
              >
                <div className="flex h-14 w-14 items-center justify-center border border-primary">
                  <Check className="h-7 w-7 text-primary" />
                </div>
                <h2 className="mt-6 font-heading text-3xl font-bold tracking-tight">Inquiry received</h2>
                <p className="mt-4 max-w-md font-mono text-sm leading-relaxed text-muted-foreground">
                  Thank you — your request has reached our team. We typically respond within one business day.
                </p>
                <button
                  data-testid="send-another-btn"
                  onClick={() => setDone(false)}
                  className="mt-8 inline-flex items-center gap-2 border border-white/20 px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:border-primary hover:text-primary"
                >
                  Send another inquiry
                </button>
              </motion.div>
            ) : (
              <Reveal>
                <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-10">
                  <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
                    <div>
                      <label className={labelClass} htmlFor="name">Name *</label>
                      <input id="name" data-testid="input-name" className={fieldClass} value={form.name} onChange={update("name")} placeholder="Jane Engineer" />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="company">Company *</label>
                      <input id="company" data-testid="input-company" className={fieldClass} value={form.company} onChange={update("company")} placeholder="Acme Robotics" />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="email">Email *</label>
                      <input id="email" type="email" data-testid="input-email" className={fieldClass} value={form.email} onChange={update("email")} placeholder="jane@acme.com" />
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="phone">Phone (optional)</label>
                      <input id="phone" data-testid="input-phone" className={fieldClass} value={form.phone} onChange={update("phone")} placeholder="+1 555 000 0000" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
                    <div>
                      <label className={labelClass}>Type of request *</label>
                      <Select value={form.request_type} onValueChange={(v) => setForm((f) => ({ ...f, request_type: v }))}>
                        <SelectTrigger data-testid="select-request-type" className="mt-1 h-auto rounded-none border-0 border-b-2 border-white/20 bg-transparent px-0 py-4 font-mono text-base focus:border-primary focus:ring-0">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="border-white/15 bg-[#0c0c0c] font-mono">
                          {REQUEST_TYPES.map((t) => (
                            <SelectItem key={t} value={t} data-testid={`request-option-${t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className={labelClass} htmlFor="region">Sourcing region (optional)</label>
                      <input id="region" data-testid="input-region" className={fieldClass} value={form.region} onChange={update("region")} placeholder="e.g. East Asia, Europe" />
                    </div>
                  </div>

                  <div>
                    <label className={labelClass} htmlFor="message">Message *</label>
                    <textarea id="message" data-testid="input-message" rows={4} className={`${fieldClass} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us about the parts, quantities, and timeline..." />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-form-submit"
                    disabled={loading}
                    className="group inline-flex items-center gap-3 bg-primary px-10 py-4 font-mono text-xs uppercase tracking-[0.2em] text-primary-foreground transition-transform duration-300 hover:-translate-y-1 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? "Sending..." : "Send inquiry"}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </button>
                </form>
              </Reveal>
            )}
          </div>

          {/* DETAILS */}
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.1}>
              <div className="border-t border-white/15 pt-8">
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight">Direct contact</h3>
                <ul className="mt-6 space-y-6 font-mono text-sm">
                  <li className="flex items-start gap-3">
                    <Mail className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <a href="mailto:contact@prescience-svcs.com" data-testid="contact-email" className="text-white/80 transition-colors hover:text-primary">contact@prescience-svcs.com</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span data-testid="contact-address" className="text-white/80">5888 West Sunset Blvd.<br />Las Vegas, NV 89118</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Globe className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span className="text-white/80">Serving engineering firms in North America — sourcing worldwide.</span>
                  </li>
                </ul>
              </div>

              <div className="mt-10 flex items-start gap-3 border border-white/10 bg-white/[0.03] p-6">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <p className="font-mono text-sm leading-relaxed text-muted-foreground">
                  We typically respond to new inquiries within <span className="text-white">one business day</span>.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
